import React, { useEffect, useState } from 'react';
import Icon from '../../components/AppIcon';
import { fetchAiAnalyticsInsight } from '../../lib/adminApi';

const TOOLS = [
  { id: 'overview', label: 'Overview' },
  { id: 'ordering_patterns', label: 'Ordering patterns' },
  { id: 'time_of_orders', label: 'Time of orders' },
  { id: 'menu_views', label: 'Menu views vs orders' },
  { id: 'caterer_performance', label: 'Caterer performance' },
];

export default function AnalyticsInsightsPage() {
  const [activeTool, setActiveTool] = useState('overview');
  const [insight, setInsight] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const text = await fetchAiAnalyticsInsight(activeTool);
        if (!cancelled) {
          setInsight(text || '');
        }
      } catch (err) {
        console.error('Failed to load AI analytics insight', err);
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load insights');
          setInsight('');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [activeTool]);

  return (
    <div className="space-y-6">
      <header className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-foreground">Analytics & AI Insights</h2>
          <p className="text-sm text-muted-foreground">
            High-level demand and supply patterns across Mrs Cater, powered by your real orders and events.
          </p>
        </div>
        <div className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs text-muted-foreground">
          <Icon name="Sparkles" size={14} />
          <span>AI summarised · SA / Africa context</span>
        </div>
      </header>

      <div className="flex flex-wrap gap-2">
        {TOOLS.map((tool) => (
          <button
            key={tool.id}
            type="button"
            onClick={() => setActiveTool(tool.id)}
            className={`inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors ${
              activeTool === tool.id
                ? 'border-primary bg-primary text-primary-foreground'
                : 'border-border bg-muted/40 text-foreground hover:bg-muted'
            }`}
          >
            {tool.id === 'overview' && <Icon name="Gauge" size={14} />}
            {tool.id === 'ordering_patterns' && <Icon name="BarChart3" size={14} />}
            {tool.id === 'time_of_orders' && <Icon name="Clock" size={14} />}
            {tool.id === 'menu_views' && <Icon name="Eye" size={14} />}
            {tool.id === 'caterer_performance' && <Icon name="Users" size={14} />}
            <span>{tool.label}</span>
          </button>
        ))}
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
          {error}
        </div>
      )}

      <section className="rounded-xl border border-border bg-card p-6 shadow-sm">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-foreground">
              {TOOLS.find((t) => t.id === activeTool)?.label || 'Insights'}
            </h3>
            <p className="mt-1 text-xs text-muted-foreground">
              Based on recent orders, events and menu activity. Use this to decide where to push demand, which caterers to coach, and what to promote.
            </p>
          </div>
          {loading && (
            <div className="inline-flex items-center gap-1 rounded-full bg-muted px-3 py-1 text-xs text-muted-foreground">
              <Icon name="Loader2" size={14} className="animate-spin" />
              <span>Thinking…</span>
            </div>
          )}
        </div>
        <div className="mt-4 rounded-lg border border-border/60 bg-background px-4 py-3 text-sm leading-relaxed text-foreground whitespace-pre-wrap">
          {!loading && !insight && !error && (
            <span className="text-muted-foreground">No insight generated yet.</span>
          )}
          {!loading && insight && <span>{insight}</span>}
        </div>
      </section>
    </div>
  );
}

